/**
 * 적립 예정 포인트 추정 훅
 * POST /api/v1/stores/{storeId}/points/estimate
 *
 * 금액 변동 시 debounce 500ms로 서버에서 적립 포인트를 계산.
 * useAvailablePoints 패턴과 동일한 stale response 방지 적용.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { pointsApi } from '@/lib/api';
import { logger } from '@/utils/logger';

export interface EstimatedPointsData {
  estimatedPoints: number;
  earnRate: number;
  note: string | null;
}

export interface UseEstimatedPointsReturn {
  data: EstimatedPointsData | null;
  isLoading: boolean;
  error: string | null;
}

export function useEstimatedPoints(
  storeId: number | undefined,
  grossAmount: number,
  tipAmount: number,
  pointsUsed: number,
  enabled: boolean = true
): UseEstimatedPointsReturn {
  const [data, setData] = useState<EstimatedPointsData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fetchIdRef = useRef(0);

  const fetchEstimate = useCallback(async (
    currentStoreId: number,
    currentGross: number,
    currentTip: number,
    currentPointsUsed: number,
  ) => {
    const currentFetchId = ++fetchIdRef.current;
    setError(null);
    setIsLoading(true);

    try {
      const response = await pointsApi.estimatePoints(currentStoreId, {
        gross_amount: currentGross,
        tip_amount: currentTip,
        points_used: currentPointsUsed,
      });

      if (fetchIdRef.current !== currentFetchId) return;

      if (response.success && response.data) {
        setData({
          estimatedPoints: response.data.estimated_points,
          earnRate: response.data.earn_rate,
          note: response.data.note,
        });
      } else {
        setData(null);
        setError('적립 포인트 정보를 불러오지 못했습니다.');
        logger.error('[useEstimatedPoints] 유효하지 않은 응답:', {
          success: response.success === true,
          hasData: response.data != null,
        });
      }
    } catch (err) {
      if (fetchIdRef.current !== currentFetchId) return;
      logger.error('[useEstimatedPoints] 조회 실패:', err);
      setData(null);
      setError(err instanceof Error ? err.message : '적립 포인트 조회 실패');
    } finally {
      if (fetchIdRef.current === currentFetchId) {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    // 의존성 변경 시 이전 데이터 초기화 + in-flight 요청 무효화
    ++fetchIdRef.current;
    setData(null);
    setError(null);

    if (!storeId || !enabled || grossAmount <= 0) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const timer = setTimeout(() => {
      if (!cancelled) {
        fetchEstimate(storeId, grossAmount, tipAmount, pointsUsed);
      }
    }, 500);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [storeId, grossAmount, tipAmount, pointsUsed, enabled, fetchEstimate]);

  return { data, isLoading, error };
}
